import moment from "moment-timezone";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { NavLink, withRouter } from "react-router-dom";
import * as Icon from "react-feather";
import _ from "lodash";

import APIManager from "../../managers/APIManager";
import Chart from "react-apexcharts";
import Card from "../common/Card";

import Page from "../common/Page";
import Table from "../common/Table";
import Loading from "../common/Loading";
import StatsBox from "../common/StatsBox";

import HelperFormatter from "../../helpers/formatter";
import HelperExcel from "../../helpers/exporter";

import { SiteActionCreators } from "../../store/actions/site";
import S from "../../store";
import dropdown from "../../assets/images/down.png";
import check from "../../assets/images/check.png";

import { Translation, useTranslation } from "react-i18next";
import { capitalizeFirst, upperCase } from "../../utils/manipulateText";

import Control from "./Control";
import LanguageSelector from "../../components/common/LanguageSelector";

import Notification from "../../components/common/Notification";
import { initialState } from "../reports/ScheduledReports/constants/actionTypes";
import ConfigModal from "../reports/ScheduledReports/ConfigModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";

const SiteDetail = (props) => {
  const { t } = useTranslation();
  const [sites, setSites] = useState();
  const [siteSelection, setSiteSelection] = useState(false);
  const adDataColumns = {
    revenue: {
      index: 0,
      code: "revenue",
      color: "#2b6cb0",
      enabled: true,
      hidden: false,
      name: "Revenue",
      padding: "42px",
      formatter: HelperFormatter.currency(props.auth.user.currency),
    },
    adRequests: {
      index: 1,
      code: "adRequests",
      color: "#c05621",
      enabled: false,
      hidden: true,
      name: "Ad Requests",
      padding: "42px",
      formatter: HelperFormatter.number,
    },
    matchedRequests: {
      index: 2,
      code: "matchedRequests",
      color: "#b83280",
      enabled: false,
      hidden: true,
      name: "Matched Ad Requests",
      padding: "20px",
      formatter: HelperFormatter.number,
    },
    views: {
      index: 3,
      code: "views",
      color: "#6b46c1",
      enabled: true,
      hidden: true,
      name: "Impressions",
      padding: "42px",
      formatter: HelperFormatter.number,
    },
    clicks: {
      index: 4,
      code: "clicks",
      color: "#b7791f",
      enabled: true,
      hidden: true,
      name: "Clicks",
      padding: "42px",
      formatter: HelperFormatter.number,
    },
    coverage: {
      index: 5,
      code: "coverage",
      color: "#c53030",
      enabled: false,
      hidden: true,
      name: "Coverage",
      padding: "42px",
      formatter: HelperFormatter.percentage,
    },
    ctr: {
      index: 6,
      code: "ctr",
      color: "#2f855a",
      enabled: true,
      hidden: true,
      name: "CTR",
      padding: "42px",
      formatter: HelperFormatter.percentage,
    },
    requestECPM: {
      index: 7,
      code: "requestECPM",
      color: "#4a5568",
      enabled: false,
      hidden: true,
      name: "Request eCPM",
      padding: "20px",
      formatter: HelperFormatter.currency(props.auth.user.currency),
    },
    eCPM: {
      index: 8,
      code: "eCPM",
      color: "#2c7a7b",
      enabled: false,
      hidden: true,
      name: "eCPM",
      padding: "42px",
      formatter: HelperFormatter.currency(props.auth.user.currency),
    },
    viewabilityReal: {
      index: 9,
      code: "viewabilityReal",
      color: "#4c51bf",
      enabled: true,
      hidden: true,
      name: "Viewability",
      padding: "42px",
      formatter: HelperFormatter.percentage,
    },
  };
  const [cache, setCache] = useState(null);
  const chart = useRef({
    adRequests: {
      options: {
        chart: {
          id: "ad-requests",
          group: "site-revenue",
        },
        colors: ["#48bb78", "#9f7aea", "#fc644c"],
        dataLabels: {
          enabled: false,
        },
        fill: {
          colors: ["#f0fff4", "#ebf4ff", "#ab5cff"],
          type: "solid",
          opacity: 1,
        },
        legend: {
          show: false,
        },
        stroke: {
          colors: ["#48bb78", "#9f7aea", "#fc644c"],
          curve: "smooth",
          width: 2,
        },
        tooltip: {
          y: [
            {
              formatter: function (y) {
                return y.toLocaleString("en-US");
              },
            },
            {
              formatter: function (y) {
                return y.toLocaleString("en-US");
              },
            },
            {
              formatter: function (y) {
                return y.toLocaleString("en-US");
              },
            },
          ],
        },
        xaxis: {
          type: "datetime",
        },
        yaxis: {
          forceNiceScale: true,
          labels: {
            minWidth: 10,
            formatter: (val) => {
              if (val >= 1000000) {
                return ((val / 1000000).toFixed(1) + "M").replace(".0", "");
              } else if (val >= 1000) {
                return ((val / 1000).toFixed(1) + "K").replace(".0", "");
              }
              return val;
            },
          },
          min: 0,
          tickAmount: 10,
        },
      },
      series: [],
    },
    revenue: {
      options: {
        chart: {
          id: "revenue",
          group: "site-revenue",
        },
        colors: ["#4299e1"],
        dataLabels: {
          enabled: false,
        },
        fill: {
          colors: ["#4299e1"],
          type: "solid",
          opacity: 1,
        },
        legend: {
          show: false,
        },
        stroke: {
          colors: ["#4299e1"],
          curve: "smooth",
          width: 2,
        },
        tooltip: {
          y: {
            formatter: (y) => {
              return y.toLocaleString("en-US", {
                currency: props.auth.user.currency,
                currencyDisplay: "code",
                style: "currency",
              });
            },
          },
        },
        xaxis: {
          type: "datetime",
        },
        yaxis: {
          forceNiceScale: false,
          labels: {
            minWidth: 10,
            formatter: (val) => {
              if (val >= 1000000) {
                return ((val / 1000000).toFixed(1) + "M").replace(".0", "");
              } else if (val >= 1000) {
                return ((val / 1000).toFixed(1) + "K").replace(".0", "");
              }
              if (val - parseInt(val) >= 0.0095) {
                return val.toFixed(2);
              }
              if (val - parseInt(val) >= 0.00095) {
                return val.toFixed(3);
              }
              return val.toFixed(2);
            },
          },
          min: () => {
            let dynamicMinToUse = 0;
            if (chart?.current?.revenue?.series?.length === 0) {
              return dynamicMinToUse;
            }
            dynamicMinToUse = chart.current.revenue.series[0].data
              .map((elem) => parseInt(elem.y))
              .reduce(function (prev, curr) {
                return prev < curr ? prev : curr;
              });
            return dynamicMinToUse;
          },
          tickAmount: 8,
        },
      },
      series: [],
    },
  });
  const currency = props?.auth?.user?.currency ?? "usd";
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [breakdowns, setBreakdowns] = useState(["daily"]);
  const [adPlacements, setAdPlacements] = useState([]);
  const adPlacementsRef = useRef([]);
  const [adPlacement, setAdPlacement] = useState(useRef());
  const [tagContent, setTagContent] = useState("0");
  const [rawData, setRawData] = useState([]);
  const [mergedReport, setMergedReport] = useState({
    breakdown: "daily",
    report: [],
  });
  const [site, setSite] = useState(null);
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("DD.MM.YYYY")
  );
  const [endDate, setEndDate] = useState(
    moment().subtract(1, "days").format("DD.MM.YYYY")
  );
  const [table, setTable] = useState({ tbl: [], dim: [] });
  const [refreshReport, setRefreshReport] = useState(false);
  const platformFilterRef = useRef("all");

  const [dimensions, setDimensions] = useState([]);
  const [selectedSiteFqdn, setSelectedSiteFqdn] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [initState, setInitState] = useState({ ...initialState });
  const [showConfig, setShowConfig] = useState(false);
  const [tableFooter, setTableFooter] = useState({});
  const [reportDimensions, setReportDimensions] = useState([]);
  const [timeoutErr, setTimeoutErr] = useState(false);
  const [serverErr, setServerErr] = useState(false);
  const sortOrder = useRef(null);
  const dimRef = useRef([]);
  const tblRef = useRef([]);
  const tableHeaders = [
    {
      content: "month",
      key: "month",
      onClick: () => sortTable("month"),
    },
    {
      content: "date",
      key: "date",
      onClick: () => sortTable("day"),
    },
    {
      content: "ad placement",
      key: "ad placement",
      onClick: () => sortTable("zone"),
    },

    {
      content: "platform",
      key: "platform",
      onClick: () => sortTable("platform"),
    },
    {
      content: "site",
      key: "site",
      onClick: () => sortTable("site"),
    },
    {
      content: "stat type",
      key: "stat type",
      onClick: () => sortTable("type"),
    },
    {
      content: "tag",
      key: "tag",
      onClick: () => sortTable("tag"),
    },
    {
      content: "app",
      key: "app",
      onClick: () => sortTable("app"),
    },
    {
      content: "analytics pageview",
      key: "analytics pageview",
      onClick: () => sortTable("analyticsPageview"),
    },
    {
      content: "analytics unique visitors",
      key: "analytics unique visitors",
      onClick: () => sortTable("analyticsUniqueVisitor"),
    },
    {
      content: "empower pageview",
      key: "virgul pageview",
      onClick: () => sortTable("virgulPageview"),
    },
    {
      content: "empower unique visitors",
      key: "virgul unique visitors",
      onClick: () => sortTable("virgulUniqueVisitor"),
    },
    {
      content: "ad req.",
      key: "ad req.",
      onClick: () => sortTable("request"),
    },
    {
      content: "matched req.",
      key: "matched req.",
      onClick: () => sortTable("match"),
    },
    {
      content: "impressions",
      key: "impressions",
      onClick: () => sortTable("view"),
    },
    {
      content: "clicks",
      key: "clicks",
      onClick: () => sortTable("click"),
    },
    {
      content: "coverage",
      key: "coverage",
      onClick: () => sortTable("coverage"),
    },
    {
      content: "ctr",
      key: "ctr",
      onClick: () => sortTable("ctr"),
    },
    {
      content: "req. ecpm",
      key: "req. ecpm",
      onClick: () => sortTable("requestEcpm"),
    },
    {
      content: "ecpm",
      key: "ecpm",
      onClick: () => sortTable("ecpm"),
    },
    {
      content: "viewability",
      key: "viewability",
      onClick: () => sortTable("viewability"),
    },
    {
      content: "revenue",
      key: "revenue",
      onClick: () => sortTable("spent"),
    },
  ];

  const toggleSiteSelect = () => {
    setSiteSelection((prev) => !prev);
  };

  const toggleSelectedIndex = (e) => {
    setSelectedSiteFqdn(e.fqdn);
    setSiteSelection(false);
    dimRef.current = e.dimensions;
    setDimensions(e.dimensions);
    setLoading(true);
    setRefreshReport((prev) => !prev);
    S.store
      .dispatch(SiteActionCreators.getSite(e.id))
      .then(() => setLoading(false));
  };

  const sortTable = (field) => {
    sortOrder.current = sortOrder.current !== "asc" ? "asc" : "desc";
    const tblTmp = table.tbl;
    if (field === "spent") {
      tblTmp.sort((a, b) => {
        const aValue =
          a.spent && a.spent[props.auth.user.currency]
            ? a.spent[props.auth.user.currency]
            : 0;
        const bValue =
          b.spent && b.spent[props.auth.user.currency]
            ? b.spent[props.auth.user.currency]
            : 0;

        if (sortOrder.current === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });

      setTable({
        ...table,
        tbl: tblTmp,
      });
    } else if (field === "ecpm") {
      tblTmp.sort((a, b) => {
        const aValue =
          a.ecpm && a.ecpm[props.auth.user.currency]
            ? a.ecpm[props.auth.user.currency]
            : 0;
        const bValue =
          b.ecpm && b.ecpm[props.auth.user.currency]
            ? b.ecpm[props.auth.user.currency]
            : 0;

        if (sortOrder.current === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });

      setTable({
        ...table,
        tbl: tblTmp,
      });
    } else if (field === "requestEcpm") {
      tblTmp.sort((a, b) => {
        const aValue =
          a.requestEcpm && a.requestEcpm[props.auth.user.currency]
            ? a.requestEcpm[props.auth.user.currency]
            : 0;
        const bValue =
          b.requestEcpm && b.requestEcpm[props.auth.user.currency]
            ? b.requestEcpm[props.auth.user.currency]
            : 0;

        if (sortOrder.current === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });

      setTable({
        ...table,
        tbl: tblTmp,
      });
    } else if (field === "viewability") {
      tblTmp.sort((a, b) => {
        const aValue =
          a.viewability && a.viewability.real ? a.viewability.real : 0;
        const bValue =
          b.viewability && b.viewability.real ? b.viewability.real : 0;

        if (sortOrder.current === "asc") {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      });

      setTable({
        ...table,
        tbl: tblTmp,
      });
    } else if (field === "date") {
      const sortedData = tblTmp
        .map((item) => {
          return {
            ...item,
            date: new Date(item.date.split(".").reverse().join("-")),
          };
        })
        .sort((a, b) => {
          if (sortOrder.current === "asc") {
            return a.date - b.date;
          } else {
            return b.date - a.date;
          }
        })
        .map((item) => {
          return {
            ...item,
            date: item.date
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("."),
          };
        });

      setTable({
        ...table,
        tbl: _.orderBy(sortedData, field, sortOrder.current),
      });
    } else {
      setTable({ ...table, tbl: _.orderBy(tblTmp, field, sortOrder.current) });
    }
  };

  const getTableHeaders = () => {
    return tableHeaders.filter((header) => {
      if (header.key === "month") {
        return reportDimensions.indexOf("monthly") !== -1;
      } else if (header.key === "date") {
        return reportDimensions.indexOf("daily") !== -1;
      } else if (header.key === "ad placement") {
        return reportDimensions.indexOf("zone") !== -1;
      } else if (header.key === "tag") {
        return reportDimensions.indexOf("tag") !== -1;
      } else if (header.key === "platform") {
        return reportDimensions.indexOf("platform") !== -1;
      } else if (header.key === "site") {
        return reportDimensions.indexOf("site") !== -1;
      } else if (header.key === "app") {
        return reportDimensions.indexOf("app") !== -1;
      } else if (header.key === "stat type") {
        return reportDimensions.indexOf("statType") !== -1;
      } else if (header.key.includes("virgul")) {
        if (
          table.dim.includes(header.key) &&
          reportDimensions.indexOf("statType") === -1
        ) {
          return true;
        }
      } else {
        if (table.dim.includes(header.key)) {
          return true;
        }
        return false;
      }
    });
  };

  let isFetchReportResolved = false;
  let isTimeoutResolved = false;

  const fetchReport = (reportParams, refreshData) => {
    return new Promise((resolve, reject) => {
      APIManager.getSiteReport(props.site, reportParams)
        .then((res) => {
          if (!isTimeoutResolved) {
            isFetchReportResolved = true;
            refreshData(res);
            resolve(res);
          }
        })
        .catch((e) => {
          if (!isTimeoutResolved) {
            reject(e);
          }
        });
    });
  };

  const timeoutPromise = new Promise((resolve) => {
    setTimeout(() => {
      if (!isFetchReportResolved) {
        isTimeoutResolved = true;
        resolve("Timeout");
      }
    }, 15000);
  });
  const getReport = (
    useCache,
    platformFilterArg = platformFilterRef.current,
    startDateArg = startDate,
    endDateArg = endDate
  ) => {
    return new Promise((resolve) => {
      var useCacheVar = useCache || false;
      const refreshData = (res) => {
        const refreshDim = APIManager.getSite(props.site).then(
          (res) => res.data.dimensions
        );
        if (!res.data || res.data.error) {
          return;
        }
        if (platformFilterArg !== "all") {
          res.data.results = res.data.results.filter(
            (value) => value.platform === platformFilterArg
          );
        }

        const report = res.data.results.reverse();
        const breakdownKey =
          breakdowns.indexOf("monthly") !== -1
            ? "month"
            : breakdowns.indexOf("daily") !== -1
            ? "day"
            : breakdowns.indexOf("zone") !== -1
            ? "zone"
            : breakdowns.indexOf("tag") !== -1
            ? "tag"
            : breakdowns.indexOf("platform") !== -1
            ? "platform"
            : breakdowns.indexOf("site") !== -1
            ? "site"
            : breakdowns.indexOf("app") !== -1
            ? "app"
            : "";

        const mergedReport = Object.values(
          report.reduce((pv, cv, ci, arr) => {
            let key = "All";
            if (breakdownKey === "day") {
              key =
                moment(
                  cv.day.split(".").reverse().join("-") + " 03:00"
                ).unix() * 1000;
            } else if (breakdownKey === "month") {
              key = cv.month;
            } else if (breakdownKey === "zone") {
              key = cv.zone.name + " (" + cv.zone.id + ")";
            } else if (breakdownKey === "app") {
              key = cv.app;
            } else if (breakdownKey === "platform") {
              key = cv.app;
            }
            pv[key] = pv[key] || {
              key: key,
              revenue: 0,
              view: 0,
              request: 0,
              match: 0,
            };
            pv[key].revenue += cv.spent[props.auth.user.currency];
            pv[key].view += cv.view;
            pv[key].request += cv.request;
            pv[key].match += cv.match;
            return pv;
          }, {})
        );
        const chartRevenue = Object.assign({}, chart.current.revenue);
        chartRevenue.tooltip = { y: [] };
        chartRevenue.options.xaxis.type =
          breakdownKey === "day" ? "datetime" : "string";
        chartRevenue.series = [];
        chartRevenue.series.push({
          name: "Revenue",
          data: mergedReport.map((result) => ({
            x: result.key,
            y: result.revenue.toFixed(2),
          })),
          type: "area",
        });
        chartRevenue.tooltip.y.push({
          formatter: adDataColumns.revenue.formatter,
        });

        const chartAdRequests = Object.assign({}, chart.current.adRequests);
        chartAdRequests.tooltip = { y: [] };
        chartAdRequests.options.xaxis.type =
          breakdownKey === "day" ? "datetime" : "string";
        chartAdRequests.series = [];

        if (mergedReport && !isNaN(mergedReport?.[0]?.request)) {
          chartAdRequests.series.push({
            name: "Ad Requests",
            data: mergedReport.map((result) => ({
              x: result.key,
              y: result.request,
            })),
            type: "line",
          });
        }

        chartAdRequests.tooltip.y.push({
          formatter: adDataColumns.adRequests.formatter,
        });

        if (mergedReport && !isNaN(mergedReport?.[0]?.match)) {
          chartAdRequests.series.push({
            name: "Matched Requests",
            data: mergedReport.map((result) => ({
              x: result.key,
              y: result.match,
            })),
            type: "line",
          });
        }

        chartAdRequests.tooltip.y.push({
          formatter: adDataColumns.matchedRequests.formatter,
        });
        chartAdRequests.series.push({
          name: "Impressions",
          data: mergedReport.map((result) => ({
            x: result.key,
            y: result.view,
          })),
          type: "line",
        });
        chartAdRequests.tooltip.y.push({
          formatter: adDataColumns.views.formatter,
        });

        setCache(res);
        chart.current.adRequests = chartAdRequests;
        chart.current.revenue = chartRevenue;
        setMergedReport({ breakdown: breakdownKey, report: mergedReport });
        setRawData(res.data.results);

        // grand total
        if (res.data.results?.length) {
          const allBreakdowns = [
            "month",
            "day",
            "tag",
            "platform",
            "app",
            "zone",
            "site",
            "statType",
          ];
          var grandTotal = {};

          const keys = [];
          const bdKeys = [];
          Object.keys(report[0]).forEach((key) => {
            if (allBreakdowns.indexOf(key) === -1) keys.push(key);
            else bdKeys.push(key);
          });
          grandTotal = { ...report[0] };
          keys.forEach((key) => (grandTotal[key] = 0));
          bdKeys.forEach((key) => (grandTotal[key] = ""));

          if (bdKeys.indexOf("site") !== -1)
            grandTotal["site"] = { name: "", id: "" };
          if (keys.indexOf("spent") !== -1)
            grandTotal["spent"] = { try: 0, eur: 0, usd: 0 };
          if (keys.indexOf("viewability") !== -1)
            grandTotal["viewability"] = { calculated: 0, real: 0 };
          if (keys.indexOf("ecpm") !== -1)
            grandTotal["ecpm"] = { try: 0, usd: 0, eur: 0 };
          if (keys.indexOf("requestEcpm") !== -1)
            grandTotal["requestEcpm"] = { try: 0, usd: 0, eur: 0 };

          report.forEach((rep) => {
            keys.forEach((key) => {
              if (key === "spent") {
                grandTotal[key]["try"] += rep[key]["try"];
                grandTotal[key]["usd"] += rep[key]["usd"];
                grandTotal[key]["eur"] += rep[key]["eur"];
              } else if (key === "viewability") {
                grandTotal[key]["calculated"] +=
                  rep[key]["calculated"] * rep["view"];
                grandTotal[key]["real"] += rep[key]["real"] * rep["view"];
              } else if (key === "ctr" || key === "coverage") {
                grandTotal[key] = rep[key];
              } else if (key === "ecpm" || key === "requestEcpm") {
                grandTotal[key] = { ...rep[key] };
              } else {
                grandTotal[key] += rep[key];
              }
            });
          });

          grandTotal[breakdownKey] = "grand total";

          if (keys.indexOf("ecpm") !== -1) {
            grandTotal["ecpm"]["try"] =
              (grandTotal["spent"]["try"] * 1000) / grandTotal["view"];
            grandTotal["ecpm"]["eur"] =
              (grandTotal["spent"]["eur"] * 1000) / grandTotal["view"];
            grandTotal["ecpm"]["usd"] =
              (grandTotal["spent"]["usd"] * 1000) / grandTotal["view"];
          }

          if (keys.indexOf("requestEcpm") !== -1) {
            grandTotal["requestEcpm"]["try"] =
              (grandTotal["spent"]["try"] * 1000) / grandTotal["request"];
            grandTotal["requestEcpm"]["eur"] =
              (grandTotal["spent"]["eur"] * 1000) / grandTotal["request"];
            grandTotal["requestEcpm"]["usd"] =
              (grandTotal["spent"]["usd"] * 1000) / grandTotal["request"];
          }

          if (keys.indexOf("viewability") !== -1) {
            grandTotal["viewability"]["calculated"] /= grandTotal["view"];
            grandTotal["viewability"]["real"] /= grandTotal["view"];
          }

          if (keys.indexOf("ctr") !== -1)
            grandTotal["ctr"] = grandTotal["click"] / grandTotal["view"];
          if (keys.indexOf("coverage") !== -1)
            grandTotal["coverage"] =
              (grandTotal["match"] / grandTotal["request"]) * 100;
          setTableFooter({ ...grandTotal });
        }
        const tmp = [].concat(res.data.results).reverse();

        tblRef.current = tmp;
        setTable({
          ...table,
          tbl: tmp,
          dim: dimRef.current,
        });
        resolve();
      };

      if (useCacheVar && cache) {
        refreshData(cache);
      } else {
        const reportParams = {
          day: breakdowns.indexOf("daily") !== -1,
          month: breakdowns.indexOf("monthly") !== -1,
          tag: false,
          tagContent: breakdowns.indexOf("tag") !== -1,
          zone: breakdowns.indexOf("zone") !== -1,
          startDate: startDateArg,
          endDate: endDateArg,
          appNames: breakdowns.indexOf("app") !== -1,
          platform: breakdowns.indexOf("platform") !== -1,
          site: breakdowns.indexOf("site") !== -1,
          platformFilter: platformFilter,
          embedAd: breakdowns.indexOf("statType") !== -1,
        };
        adPlacementsRef.current.forEach((val, ind, arr) => {
          reportParams["zoneIds[" + ind + "]"] = parseInt(val);
        });
        if (platformFilterArg !== "all") {
          reportParams.platform = true;
        }
        Promise.race([fetchReport(reportParams, refreshData), timeoutPromise])
          .then((result) => {
            if (result === "Timeout") {
              setTimeoutErr(true);
              if (!isFetchReportResolved) {
                APIManager.reportsFallback(
                  { ...reportParams },
                  props.selectedSite.virgulId,
                  props.auth.user.email,
                  props.auth.user.currency,
                  props.auth.user.integrationType === 3 ||
                    props.auth.user.integrationType === 4
                    ? "app"
                    : "site"
                );
              }
            }
          })
          .catch((error) => {
            console.error("Error:", error);
            setServerErr(true);
          });
      }
    });
  };

  const footerSection =
    Object.keys(tableFooter).length > 0 ? (
      <Table.Row key={Math.random()} className="bg-gray-200">
        {reportDimensions.indexOf("monthly") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong>{upperCase(t(tableFooter?.month))}</strong>
          </Table.Col>
        ) : null}
        {reportDimensions.indexOf("daily") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong>{upperCase(t(tableFooter?.day))}</strong>
          </Table.Col>
        ) : null}
        {reportDimensions.indexOf("zone") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong data-zone={tableFooter?.zone?.id}>
              {tableFooter?.zone?.id &&
                `${tableFooter?.zone?.name} (${tableFooter?.zone?.id})`}
            </strong>
          </Table.Col>
        ) : null}
        {reportDimensions.indexOf("tag") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong data-zone={tableFooter?.tag}>
              {upperCase(t(tableFooter?.tag))}
            </strong>
          </Table.Col>
        ) : null}
        {reportDimensions.indexOf("platform") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong data-zone={tableFooter?.platform}>
              {upperCase(t(tableFooter?.platform))}
            </strong>
          </Table.Col>
        ) : null}
        {reportDimensions.indexOf("site") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong data-zone={tableFooter?.site?.name}>
              {upperCase(t(tableFooter?.site?.name))}
            </strong>
          </Table.Col>
        ) : null}
        {reportDimensions.indexOf("statType") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong data-zone={tableFooter?.site?.name}>
              {upperCase(t(tableFooter?.site?.name))}
            </strong>
          </Table.Col>
        ) : null}
        {reportDimensions.indexOf("app") !== -1 ? (
          <Table.Col className="border text-center uppercase">
            <strong>{tableFooter?.app}</strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("analytics pageview") ? (
          <Table.Col className="border text-right">
            <strong>
              {HelperFormatter.number(tableFooter.analyticsPageview)}
            </strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("analytics unique visitors") ? (
          <Table.Col className="border text-right">
            <strong>
              {HelperFormatter.number(tableFooter.analyticsUniqueVisitor)}
            </strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("virgul pageview") &&
        reportDimensions.indexOf("statType") === -1 ? (
          <Table.Col className="border text-right">
            <strong>
              {HelperFormatter.number(tableFooter.virgulPageview)}
            </strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("virgul unique visitors") &&
        reportDimensions.indexOf("statType") === -1 ? (
          <Table.Col className="border text-right">
            <strong>
              {HelperFormatter.number(tableFooter.virgulUniqueVisitor)}
            </strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("ad req.") ? (
          <Table.Col className="border text-right">
            <strong>{HelperFormatter.number(tableFooter.request)}</strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("matched req.") ? (
          <Table.Col className="border text-right">
            <strong>{HelperFormatter.number(tableFooter.match)}</strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("impressions") ? (
          <Table.Col className="border text-right">
            <strong>{HelperFormatter.number(tableFooter.view)}</strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("clicks") ? (
          <Table.Col className="border text-right">
            <strong>{HelperFormatter.number(tableFooter.click)}</strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("coverage") ? (
          <Table.Col className="border text-right">
            <strong>{HelperFormatter.percentage(tableFooter.coverage)}</strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("ctr") ? (
          <Table.Col className="border text-right">
            <strong>
              {HelperFormatter?.percentage(tableFooter?.ctr * 100)}
            </strong>
          </Table.Col>
        ) : null}
        {table?.dim?.includes("req. ecpm") ? (
          <Table.Col className="border text-right">
            <strong>
              {tableFooter?.requestEcpm &&
                HelperFormatter?.currency(props?.auth?.user?.currency)(
                  tableFooter?.requestEcpm[props?.auth?.user?.currency]
                )}
            </strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("ecpm") ? (
          <Table.Col className="border text-right">
            <strong>
              {HelperFormatter.currency(props.auth.user.currency)(
                tableFooter.ecpm[props.auth.user.currency]
              )}
            </strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("viewability") ? (
          <Table.Col className="border text-right">
            <strong>
              {tableFooter.viewability &&
                HelperFormatter.percentage(tableFooter.viewability.real * 100)}
            </strong>
          </Table.Col>
        ) : null}
        {table.dim.includes("revenue") ? (
          <Table.Col className="border text-right">
            <strong>
              {HelperFormatter.currency(props.auth.user.currency)(
                tableFooter.spent[props.auth.user.currency]
              )}
            </strong>
          </Table.Col>
        ) : null}
      </Table.Row>
    ) : null;

  const getSite = async () => {
    const res = await APIManager.getSite(props.site);
    if (initRender.current) {
      setTable({ tbl: tblRef.current, dim: res.data.dimensions });
      initRender.current = false;
    }
    setSite(res.data);
    setDimensions(res.data.dimensions);
    dimRef.current = res.data.dimensions;
    return res.data;
  };

  const initRender = useRef(true);

  useEffect(() => {
    APIManager.getSites().then((res) => {
      if (res.data.length === 0) {
        return;
      }
      setSiteId(props.site);
      if (res.data.length > 1) {
        var firstSite = res.data[res.data.length - 1];
        var head = res.data.slice(0, res.data.length - 1);
        var shifted = [firstSite].concat(head);
        setSites(shifted);
        var tmp = res.data.filter((item) => item.id === props.site);
        setSelectedSiteFqdn(tmp[0].fqdn);
        if (props.site === undefined) {
          setSiteId(tmp[0].id);
        }
      } else {
        setSites(res.data);
        if (props.site === undefined) {
          setSiteId(res.data.sites[0].id);
        }
      }
    });
    setLoading(true);
    setTimeoutErr(false);
    setServerErr(false);
    setReportDimensions([...breakdowns]);
    getSite().then(() => {
      getReport().then(() => {
        setLoading(false);
      });
    });
  }, [refreshReport]);

  useEffect(() => {
    if (siteId === props.site) {
      return;
    }
    setSiteId(props.site);
    getSite().then(() => {
      getReport().then(() => {
        setLoading(false);
      });
    });
  });

  const toggleFilters = () => {
    const filters = document.querySelector(".filters");
    filters.classList.toggle("hidden");
    filters.classList.toggle("flex");
    filters.classList.toggle("flex-row");
  };

  let pageContent = null;
  const filterClass = window.innerWidth < 1440 ? "float-left" : "float-right";
  if (!loading) {
    if (site.onboardingStep >= 0) {
      props.history.push(`/sites/${site.id}/onboarding`);
      return null;
    }

    const adRequestData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.request,
    }));
    const matchRequestData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.match,
    }));
    const viewsData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.view,
    }));
    const totalRevenueData = mergedReport.report.map((row) => ({
      x: row.key,
      y: row.revenue,
    }));

    if (!totalRevenueData.length) {
      pageContent = (
        <div className="py-4 text-orange-600 text-md text-center">
          <Icon.Info className="inline mr-2" width="16" height="16" />
          <Translation>
            {(t) =>
              capitalizeFirst(
                t("there is no data to show for this time period.")
              )
            }
          </Translation>
        </div>
      );
    } else {
      pageContent = (
        <>
          <div class="md:grid md:grid-cols-5 md:gap-x-2">
            <div class="grid grid-cols-2 gap-x-2 md:grid-cols-1 md:gap-x-2 md:col-span-1">
              <div
                className="md:flex min-w-fit"
                style={{ height: "fit-content", minWidth: "145px" }}
              >
                {totalRevenueData.length > 0 && (
                  <StatsBox
                    series={[
                      {
                        name: "Revenue",
                        data: totalRevenueData,
                        type: "area",
                      },
                    ]}
                    label="total revenue"
                    displayChart={false}
                    valueFn={(data) =>
                      data.map((v) => v.y).reduce((a, v) => a + v)
                    }
                    formatter={adDataColumns.revenue.formatter}
                    xaxisType={
                      mergedReport.breakdown === "day" ? "datetime" : "string"
                    }
                  />
                )}
              </div>
              {adRequestData.length > 0 && !isNaN(adRequestData[0].y) && (
                <div
                  className="md:flex min-w-fit"
                  style={{ height: "fit-content", minWidth: "145px" }}
                >
                  <StatsBox
                    series={[
                      {
                        name: "Ad Request",
                        data: adRequestData,
                        type: "area",
                      },
                    ]}
                    label="total ad requests"
                    displayChart={false}
                    valueFn={(data) =>
                      data.map((v) => v.y).reduce((a, v) => a + v)
                    }
                    formatter={adDataColumns.adRequests.formatter}
                    xaxisType={
                      mergedReport.breakdown === "day" ? "datetime" : "string"
                    }
                  />
                </div>
              )}
              {matchRequestData.length > 0 && !isNaN(matchRequestData[0].y) && (
                <div
                  className="md:flex min-w-fit"
                  style={{ height: "fit-content", minWidth: "145px" }}
                >
                  <StatsBox
                    series={[
                      {
                        name: "Match Request",
                        data: matchRequestData,
                        type: "area",
                      },
                    ]}
                    label="total match requests"
                    displayChart={false}
                    valueFn={(data) =>
                      data.map((v) => v.y).reduce((a, v) => a + v)
                    }
                    formatter={adDataColumns.adRequests.formatter}
                    xaxisType={
                      mergedReport.breakdown === "day" ? "datetime" : "string"
                    }
                  />
                </div>
              )}
              <div
                className="md:flex min-w-fit"
                style={{ height: "fit-content", minWidth: "145px" }}
              >
                {viewsData.length > 0 && (
                  <StatsBox
                    series={[
                      {
                        name: "Impressions",
                        data: viewsData,
                        type: "area",
                      },
                    ]}
                    label="total impressions"
                    displayChart={false}
                    valueFn={(data) =>
                      data.map((v) => v.y).reduce((a, v) => a + v)
                    }
                    formatter={adDataColumns.views.formatter}
                    xaxisType={
                      mergedReport.breakdown === "day" ? "datetime" : "string"
                    }
                  />
                )}
              </div>
            </div>
            <div class="md:col-span-2">
              <Card title="revenue">
                <div className="p-2">
                  <Chart
                    type={chart.current.revenue.type}
                    height="345"
                    options={chart.current.revenue.options}
                    series={chart.current.revenue.series}
                  />
                </div>
              </Card>
            </div>
            <div class="md:col-span-2">
              <Card title="Impressions">
                <div className="p-2">
                  <Chart
                    type={chart.current.adRequests.type}
                    height="345"
                    options={chart.current.adRequests.options}
                    series={chart.current.adRequests.series}
                  />
                </div>
              </Card>
            </div>
          </div>
          <Card title="report">
            <div className="float-right flex ">
              <button
                style={{ marginTop: "-30px" }}
                onClick={() => {
                  HelperExcel.exportExcel(
                    [].concat(table.tbl),
                    "DailyBreakdown",
                    true,
                    currency
                  );
                }}
                className="mx-4 mt-4 pull-right text-sm py-1"
              >
                <Icon.Download
                  className="pull-left mt-1 mr-1 text-gray-600"
                  width="16"
                  height="16"
                />
                <label
                  style={{
                    float: "right",
                    color: "#718096",
                    cursor: "pointer",
                    marginTop: "1px",
                  }}
                >
                  /<Translation>{(t) => upperCase(t("download"))}</Translation>
                </label>
              </button>
              <button
                style={{ marginTop: "-30px" }}
                onClick={() => {
                  var data = HelperExcel.exportExcel(
                    [].concat(table.tbl),
                    "DailyBreakdown",
                    true,
                    currency,
                    true
                  );
                  const fileName =
                    site.fqdn + " - " + new Date().toLocaleDateString();
                  APIManager.exportToDrive(fileName, data).then((res) => {
                    window.open(res.url, "_blank");
                  });
                }}
                className="mx-4 mt-4 pull-right text-sm py-1"
              >
                <FontAwesomeIcon
                  icon={faGoogleDrive}
                  style={{
                    width: "16px",
                    height: "16px",
                  }}
                  className="pull-left mt-1 mr-1 text-gray-600"
                />
                <label
                  style={{
                    float: "right",
                    color: "#718096",
                    cursor: "pointer",
                    marginTop: "1px",
                  }}
                >
                  /
                  <Translation>
                    {(t) => upperCase(t("open in drive"))}
                  </Translation>
                </label>
              </button>
            </div>

            <div className="overflow-x-scroll">
              <Table headers={getTableHeaders()} footerSection={footerSection}>
                {table.tbl.map((row, index) => (
                  <Table.Row
                    key={Math.random()}
                    className="even:bg-gray-100 hover:bg-gray-200"
                  >
                    {reportDimensions.indexOf("monthly") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong>{row?.month}</strong>
                      </Table.Col>
                    ) : null}
                    {reportDimensions.indexOf("daily") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong>{row?.day}</strong>
                      </Table.Col>
                    ) : null}
                    {reportDimensions.indexOf("zone") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong data-zone={row?.zone?.id}>
                          {row?.zone?.id &&
                            `${row?.zone?.name} (${row?.zone?.id})`}
                        </strong>
                      </Table.Col>
                    ) : null}
                    {reportDimensions.indexOf("tag") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong data-zone={row?.tag}>{row?.tag}</strong>
                      </Table.Col>
                    ) : null}
                    {reportDimensions.indexOf("platform") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong data-zone={row?.platform}>
                          {row?.platform}
                        </strong>
                      </Table.Col>
                    ) : null}
                    {reportDimensions.indexOf("site") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong data-zone={row?.site?.name}>
                          {row?.site?.name}
                        </strong>
                      </Table.Col>
                    ) : null}
                    {reportDimensions.indexOf("statType") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong data-zone={row?.type}>{row?.type}</strong>
                      </Table.Col>
                    ) : null}
                    {breakdowns.indexOf("app") !== -1 ? (
                      <Table.Col className="border text-center">
                        <strong>{row?.app}</strong>
                      </Table.Col>
                    ) : null}

                    {table.dim.includes("analytics pageview") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.analyticsPageview)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("analytics unique visitors") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.analyticsUniqueVisitor)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("virgul pageview") &&
                    reportDimensions.indexOf("statType") === -1 ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.virgulPageview)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("virgul unique visitors") &&
                    reportDimensions.indexOf("statType") === -1 ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.virgulUniqueVisitor)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("ad req.") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.request)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("matched req.") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.match)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("impressions") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.view)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("clicks") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.number(row.click)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("coverage") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.percentage(row.coverage)}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("ctr") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter?.percentage(row?.ctr * 100)}
                      </Table.Col>
                    ) : null}
                    {table?.dim?.includes("req. ecpm") ? (
                      <Table.Col className="border text-right">
                        {row.requestEcpm &&
                          HelperFormatter?.currency(
                            props?.auth?.user?.currency
                          )(row.requestEcpm[props.auth.user.currency])}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("ecpm") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.currency(props.auth.user.currency)(
                          row.ecpm[props.auth.user.currency]
                        )}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("viewability") ? (
                      <Table.Col className="border text-right">
                        {row.viewability &&
                          HelperFormatter.percentage(
                            row.viewability.real * 100
                          )}
                      </Table.Col>
                    ) : null}
                    {table.dim.includes("revenue") ? (
                      <Table.Col className="border text-right">
                        {HelperFormatter.currency(props.auth.user.currency)(
                          row.spent[props.auth.user.currency]
                        )}
                      </Table.Col>
                    ) : null}
                  </Table.Row>
                ))}
              </Table>
            </div>
          </Card>
        </>
      );
    }
  }

  return (
    <>
      <ConfigModal
        isVisible={showConfig}
        onClose={() => {
          setShowConfig(false);
          if (
            initState.selectedSite === "" ||
            initState.selectedSite === "all-sites"
          ) {
            setInitState({
              ...initialState,
              siteBreakdowns: initState.siteBreakdowns,
              sitePlatforms: initState.sitePlatforms,
              selectedSite: initState.selectedSite,
              selectedSitePlacements: initState.selectedSitePlacements,
              selectedUserApps: initState.selectedUserApps,
              selectedUserCountries: initState.selectedUserCountries,
              selectedPlatforms:
                props.auth.user.integrationType === 3 ||
                props.auth.user.integrationType === 4
                  ? []
                  : ["all"],
              selectedBreakdowns:
                props.auth.user.integrationType === 3 ||
                props.auth.user.integrationType === 4
                  ? ["appNames"]
                  : ["daily"],
            });
          } else {
            setInitState({
              ...initialState,
            });
          }
          const bodyEl = document.getElementsByTagName("BODY")[0];
          bodyEl.style.overflow = "hidden";
        }}
        integrationType={props.auth.user.integrationType}
        initialState={initState}
      />

      <Page
        controlClass="w-full items-center justify-between"
        title={
          site &&
          sites?.length > 0 && (
            <>
              <div className="flex justify-between relative">
                <div
                  style={{ width: "300px" }}
                  onClick={(e) => toggleSiteSelect()}
                  id="siteSelector"
                  className={
                    "pr-3 py-2 mb-1 rounded-md cursor-pointer hover:text-indigo-500 outline-none flex items-center justify-between text-md font-semibold"
                  }
                >
                  <div className="inline-flex">
                    <span
                      id="selectedSiteTitle"
                      className="ml-2 inline group-hover:inline text-xl"
                    >
                      {selectedSiteFqdn || site.fqdn}
                    </span>
                  </div>
                  {sites.length > 1 && (
                    <img
                      style={{ width: "25px" }}
                      alt=""
                      className="float-right mr-2"
                      src={dropdown}
                    />
                  )}
                </div>

                <div
                  style={{ width: "270px", top: "4rem" }}
                  className={` ${
                    siteSelection === true ? "absolute" : "hidden"
                  } absolute z-20 px-3 py-2 mb-1 rounded-md cursor-pointer hover:text-indigo-500 outline-none items-center justify-between text-md font-semibold bg-white`}
                >
                  {sites.map((otherSite, ind) => (
                    <NavLink
                      onClick={(e) => {
                        toggleSelectedIndex(otherSite);
                        setBreakdowns(["daily"]);
                        setPlatformFilter("all");
                        adPlacementsRef.current = [];
                        setAdPlacements([]);
                      }}
                      key={otherSite.fqdn}
                      to={`/sites/${otherSite.id}`}
                      className={
                        "px-3 py-2 mb-1 rounded-md cursor-pointer hover:text-indigo-500 outline-none flex items-center justify-between text-md font-semibold text-gray-600"
                      }
                    >
                      <div className="flex">
                        <span className="ml-2 inline group-hover:inline">
                          {otherSite.fqdn}
                        </span>
                      </div>
                      {otherSite.id === siteId && (
                        <img
                          style={{ width: "20px" }}
                          alt=""
                          className="float-right mr-1"
                          src={check}
                        />
                      )}
                    </NavLink>
                  ))}
                </div>

                <div className="md:hidden flex">
                  <button className={"filter-icon"} onClick={toggleFilters}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.29289 1.29289C1.10536 1.48043 1 1.73478 1 2V4.586C1.00006 4.85119 1.10545 5.10551 1.293 5.293L7.707 11.707C7.89455 11.8945 7.99994 12.1488 8 12.414V19L12 15V12.414C12.0001 12.1488 12.1055 11.8945 12.293 11.707L18.707 5.293C18.8946 5.10551 18.9999 4.85119 19 4.586V2C19 1.73478 18.8946 1.48043 18.7071 1.29289C18.5196 1.10536 18.2652 1 18 1H2C1.73478 1 1.48043 1.10536 1.29289 1.29289Z"
                        stroke="#728195"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <LanguageSelector />
              </div>
              <Notification data={site} />
            </>
          )
        }
        controls={
          <Control
            filterClass={filterClass}
            state={{
              site,
              platformFilter,
              adPlacement,
              startDate,
              endDate,
              adPlacements,
              setAdPlacements,
              setCache,
              setLoading,
              setPlatformFilter,
              setEndDate,
              setStartDate,
            }}
            setShowConfig={setShowConfig}
            initState={initState}
            setInitState={setInitState}
            site={site}
            platformFilterRef={platformFilterRef}
            adPlacementsRef={adPlacementsRef}
            getReport={getReport}
            setRefreshReport={setRefreshReport}
            breakdowns={breakdowns}
            setBreakdowns={setBreakdowns}
          />
        }
      >
        {timeoutErr ? (
          <div className="w-full m-auto py-4 text-orange-600 text-md text-center">
            <Icon.Info className="inline mr-2" width="16" height="16" />
            {capitalizeFirst(
              t(
                "Your report took a long time to generate. You can check its status on the reports page."
              )
            )}
          </div>
        ) : serverErr ? (
          <div className="w-full m-auto py-4 text-orange-600 text-md text-center">
            <Icon.Info className="inline mr-2" width="16" height="16" />
            {capitalizeFirst(
              t(
                "A server side error has occured. Please try refreshing the report again."
              )
            )}
          </div>
        ) : (
          <>
            {loading && <Loading />}
            {pageContent}
          </>
        )}
      </Page>
    </>
  );
};

SiteDetail.propTypes = {
  auth: PropTypes.object.isRequired,
  history: PropTypes.object,
  match: PropTypes.object,
  site: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    selectedSite: state.site.selectedSite,
  };
};

export default connect(mapStateToProps)(withRouter(SiteDetail));
